import React, { useState, useEffect } from 'react';
import { Achievement } from '../../types/goals';
import { IconSVG } from '../UI';
import { useTranslation } from 'react-i18next';
import './styles.css';

interface AchievementNotificationProps {
  achievement: Achievement;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseTime?: number;
}

const AchievementNotification: React.FC<AchievementNotificationProps> = ({
  achievement,
  onClose,
  autoClose = true,
  autoCloseTime = 5000
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Show with animation after a small delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    // Auto close after specified time if autoClose is true
    let closeTimer: NodeJS.Timeout | null = null;
    if (autoClose) {
      closeTimer = setTimeout(() => {
        setIsVisible(false);
        // Allow animation to complete before removing
        setTimeout(onClose, 500);
      }, autoCloseTime);
    }
    
    return () => {
      clearTimeout(showTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [autoClose, autoCloseTime, onClose]);
  
  const handleClose = () => {
    setIsVisible(false);
    // Allow animation to complete before removing
    setTimeout(onClose, 500);
  };
  
  const getCategoryIcon = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'training':
        return 'dumbbell';
      case 'competition':
        return 'trophy';
      case 'consistency':
        return 'repeat';
      case 'technique':
        return 'star';
      case 'special':
        return 'award';
      default:
        return 'badge';
    }
  };
  
  return (
    <div 
      className={`achievement-notification ${isVisible ? 'visible' : ''}`}
      role="alert"
    >
      <div className="notification-content">
        <div className="icon-container">
          {achievement.iconUrl ? (
            <img 
              src={achievement.iconUrl} 
              alt="Achievement" 
              className="achievement-icon"
            />
          ) : (
            <IconSVG 
              icon={getCategoryIcon(achievement.category)}
              className="achievement-default-icon"
            />
          )}
        </div>
        <div className="text-content">
          <div className="achievement-title">{t('achievements.unlocked')}!</div>
          <div className="achievement-name">{achievement.name}</div>
          {achievement.points && (
            <div className="achievement-points">+{achievement.points} {t('achievements.points')}</div>
          )}
        </div>
        <button className="close-button" onClick={handleClose}>
          <IconSVG icon="close" className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default AchievementNotification;